import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Filter, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { blogService } from '../../services';
import { buildCategoryTree } from '../../utils/blog';
import { LoadingSpinner } from '../../components/ui/Loading';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import PageContainer from '../../components/PageContainer';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {getStatusTranslation, getVisibilityTypeTranslation} from "../../utils/blogUtils";
import { isAdminEquivalent } from '../../utils/permissions';

const AdminBlog = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState('');
  const [visibility, setVisibility] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, postId: null });

  useEffect(() => {
    blogService.getCategories()
      .then((res) => setCategories(res.categories || []))
      .catch(() => setCategories([]));
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      setLoading(true);
      const res = await blogService.getPosts({
        page,
        limit: 20,
        search: search.trim() || undefined,
        categoryId: categoryId || undefined,
        status: status || undefined,
        visibility: visibility || undefined,
      });
      setPosts(res.posts || []);
      setTotalPages(res.pagination?.totalPages || 1);
    } catch (e) {
      console.error('Erro ao carregar posts:', e);
    } finally {
      setLoading(false);
    }
  }, [page, search, categoryId, status, visibility]);

  useEffect(() => {
    if (isAdminEquivalent(user?.role)) loadPosts();
  }, [user, loadPosts]);

  const tree = buildCategoryTree(categories);

  const updateFilter = (setter) => (value) => {
    setPage(1);
    setter(value);
  };

  const confirmDelete = async () => {
    const postId = confirmModal.postId;
    setConfirmModal({ isOpen: false, postId: null });
    try {
      await blogService.deletePost(postId);
      notify.success('Post excluído com sucesso!', '🗑️ Post Removido');
      await loadPosts();
    } catch (e) {
      notify.error('Erro ao excluir post. Tente novamente.', 'Erro');
    }
  };

  if (!isAdminEquivalent(user?.role)) {
    return (
      <PageContainer title="Gerenciar Blog">
        <p className="text-red-400">Acesso negado. Apenas administradores.</p>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title="Gerenciar Blog"
      description="Publique e edite os posts do clã"
      actions={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => navigate('/app/admin/blog/categories')}>
            Categorias
          </Button>
          <Button onClick={() => navigate('/app/admin/blog/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Post
          </Button>
        </div>
      }
    >
      {/* Filters */}
      <Card variant="glass" className="mb-8">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <Filter className="w-5 h-5 text-cyan-400" />
            <h3 className="text-lg font-semibold text-white">Filtros</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Input
              icon={Search}
              placeholder="Buscar por título..."
              value={search}
              onChange={(e) => updateFilter(setSearch)(e.target.value)}
              variant="glass"
            />

            <select
              value={categoryId}
              onChange={(e) => updateFilter(setCategoryId)(e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todas as categorias</option>
              {tree.map((parent) => (
                <optgroup key={parent.id} label={parent.name}>
                  <option value={parent.id} className="bg-slate-800">{parent.name}</option>
                  {parent.children.map((child) => (
                    <option key={child.id} value={child.id} className="bg-slate-800">&nbsp;&nbsp;{child.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <select
              value={status}
              onChange={(e) => updateFilter(setStatus)(e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todos os status</option>
              <option value="DRAFT" className="bg-slate-800">Rascunho</option>
              <option value="PUBLISHED" className="bg-slate-800">Publicado</option>
            </select>

            <select
              value={visibility}
              onChange={(e) => updateFilter(setVisibility)(e.target.value)}
              className="bg-white/5 border border-white/10 text-white rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 transition-all"
            >
              <option value="" className="bg-slate-800">Todas as visibilidades</option>
              <option value="ADMIN_ONLY" className="bg-slate-800">Somente admin</option>
              <option value="INTERNAL" className="bg-slate-800">Interno</option>
              <option value="PUBLIC" className="bg-slate-800">Público</option>
            </select>
          </div>
        </div>
      </Card>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      ) : posts.length === 0 ? (
        <Card variant="glass">
          <div className="p-6 text-center text-gray-400">Nenhum post cadastrado.</div>
        </Card>
      ) : (
        <Card variant="glass">
          <div className="divide-y divide-white/10">
            {posts.map((post) => (
              <div key={post.id} className="flex items-center justify-between p-4">
                <div>
                  <div className="text-white font-medium">{post.title}</div>
                  <div className="text-xs text-gray-400">
                    {post.category?.name || 'Sem categoria'} · {getStatusTranslation(post.status)} · {getVisibilityTypeTranslation(post.visibility)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Link to={`/app/admin/blog/${post.id}/edit`}>
                    <Button variant="outline" size="sm">Editar</Button>
                  </Link>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => setConfirmModal({ isOpen: true, postId: post.id })}
                  >
                    Excluir
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-8 gap-2">
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>← Anterior</Button>
          <span className="px-4 py-2 text-sm text-gray-400">Página {page} de {totalPages}</span>
          <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Próxima →</Button>
        </div>
      )}

      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, postId: null })}
        onConfirm={confirmDelete}
        title="Excluir Post"
        message="Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </PageContainer>
  );
};

export default AdminBlog;
